import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { 
  Camera, Video, X, ExternalLink 
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/Gallery.scss';

export default function Gallery() {
  const location = useLocation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const { t } = useTranslation();

  const navItems = [
    { id: 'photo-gallery', label: t('nav_photo_gallery') },
    { id: 'video-gallery', label: t('nav_video_gallery') },
    { id: 'annual-function', label: t('nav_annual_function') },
    { id: 'sports-cultural', label: t('nav_sports_cultural') },
    { id: 'tours-events', label: t('nav_tours_events') },
  ];

  const galleryItems = [
    {
      id: 1,
      title: 'School Campus Front Elevation',
      category: 'campus',
      src: '/descriptive-image/school-building-front-view.jpg',
      desc: 'Architectural front view of Prabhat Tara Vidya Niketan main academic block in Rajkhand, Goraul.'
    },
    {
      id: 2,
      title: 'Digital Computer Lab & Coding Sessions',
      category: 'academics',
      src: '/descriptive-image/students-learning-with-laptops-classroom.png',
      desc: 'Students engaged in practical computing, internet research, and scratch programming under faculty supervision.'
    },
    {
      id: 3,
      title: 'Indian Parliament Exhibition Models',
      category: 'tours-events',
      src: '/descriptive-image/students-with-parliament-models1.png',
      desc: 'Senior students presenting handmade architectural models of the Indian Parliament during social science day.'
    },
    {
      id: 4,
      title: 'Art, Drawing & Certificate Distribution',
      category: 'annual-function',
      src: '/descriptive-image/students-holding-certificates-drawings-school.jpg',
      desc: 'Young artists proudly showcasing their vibrant paintings and merit certificates during the annual prize distribution.'
    },
    {
      id: 5,
      title: 'National Deworming & Health Day',
      category: 'tours-events',
      src: '/descriptive-image/nation-deworming-day-celebration.png',
      desc: 'Health and wellness initiative conducted in collaboration with health officials for student well-being.'
    },
    {
      id: 6,
      title: 'Secondary Board Examination Toppers',
      category: 'sports-cultural',
      src: '/descriptive-image/school-toppers-class-x-congratulations-2.png',
      desc: 'Felicitation ceremony recognizing Class 10 board toppers who achieved 100% pass distinction.'
    },
    {
      id: 7,
      title: '30+ Years Service & Foundation Milestone',
      category: 'annual-function',
      src: '/descriptive-image/thirty-years-service-award.png',
      desc: 'Celebrating three decades of dedicated service to education and community development in Vaishali.'
    },
    {
      id: 8,
      title: 'Pre-Primary Kids Activity Area',
      category: 'campus',
      src: '/images/playclass.jpeg',
      desc: 'Vibrant play school classroom designed with sensory games, colorful toys, and activity blocks.'
    },
    {
      id: 9,
      title: 'Main Academic Building & Assembly Grounds',
      category: 'campus',
      src: '/images/building.jpg',
      desc: 'View of the school campus courtyard where morning assemblies, yoga, and drills take place.'
    }
  ];

  const filteredPhotos = filter === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === filter);

  return (
    <div className="gallery-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>Gallery - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Explore photo and video gallery of Prabhat Tara Vidya Niketan: Annual functions, Sports day, Cultural activities, Educational exhibitions, and Campus infrastructure."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/gallery" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <span className="badge">
          <Camera size={16} /> Campus Moments & Celebrations
        </span>
        <h1>School Gallery</h1>
        <p>
          A vibrant visual tapestry capturing academic achievements, sports triumphs, cultural pageantry, and joyous school memories.
        </p>
      </div>

      {/* Nav Pills Bar */}
      <div className="nav-pills-bar">
        <div className="nav-container">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div className="main-content">
        
        {/* 1. PHOTO GALLERY */}
        <section id="photo-gallery" className="gallery-section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <span className="card-tag">1. Visual Archive</span>
              <h2>Photo Gallery</h2>
              <p style={{ color: '#556987', margin: 0 }}>Browse photographs across all school categories.</p>
            </div>

            {/* Filter Pills */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { id: 'all', label: 'All Photos' },
                { id: 'campus', label: 'Campus & Facilities' },
                { id: 'academics', label: 'Academics & Labs' },
                { id: 'annual-function', label: 'Annual Functions' },
                { id: 'tours-events', label: 'Exhibitions & Events' },
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setFilter(cat.id)}
                  style={{
                    padding: '0.45rem 0.9rem',
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    background: filter === cat.id ? '#011673' : '#fff',
                    color: filter === cat.id ? '#fff' : '#011673',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="photo-grid">
            {filteredPhotos.map((item) => (
              <div 
                key={item.id} 
                className="photo-card"
                onClick={() => setSelectedImage(item)}
              >
                <div className="img-wrapper">
                  <img src={item.src} alt={item.title} loading="lazy" />
                  <span className="category-badge">{item.category}</span>
                </div>
                <div className="caption-box">
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 2. VIDEO GALLERY */}
        <section id="video-gallery" className="gallery-section-card">
          <span className="card-tag">2. Dynamic Media</span>
          <h2>Video Gallery</h2>
          <p className="card-desc">
            Experience campus life in motion through video tours, annual day highlights, and student cultural performances.
          </p>

          <div className="video-grid">
            <div className="video-card">
              <video controls preload="metadata" poster="/descriptive-image/school-building-front-view.jpg">
                <source src="/ptvn_hero.mp4" type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
              <div className="video-info">
                <h4>PTVN Campus Journey & Welcome Video</h4>
                <p>Welcome curtains opening ceremony and campus tour showcasing infrastructure and facilities.</p>
              </div>
            </div>

            <div className="video-card">
              <video controls preload="metadata" poster="/images/building.jpg">
                <source src="/ptvn_heros.mp4" type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
              <div className="video-info">
                <h4>Campus Activities & Learning Highlights</h4>
                <p>Watch our students in the computer laboratory, science rooms, and morning assembly in motion.</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '2rem', padding: '1.25rem', background: '#f0f4ff', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <strong style={{ color: '#011673', display: 'block', fontSize: '1rem', marginBottom: '0.2rem' }}>Watch More on Our Official YouTube Channel</strong>
              <span style={{ color: '#556987', fontSize: '0.9rem' }}>Subscribe to @ptvn_goraul for full event recordings, speeches, and festivals.</span>
            </div>
            <a 
              href="https://www.youtube.com/@ptvn_goraul" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#dc2626', color: '#fff', padding: '0.6rem 1.2rem', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '0.9rem' }}
            >
              <Video size={16} /> Open YouTube Channel <ExternalLink size={14} />
            </a>
          </div>
        </section>

        {/* 3. ANNUAL FUNCTION GALLERY */}
        <section id="annual-function" className="gallery-section-card">
          <span className="card-tag">3. Stage & Celebrations</span>
          <h2>Annual Function & Cultural Extravaganza</h2>
          <p className="card-desc">
            The annual gathering is the pinnacle of our school calendar, where students take the stage with theatrical dramas, folk dances, musical recitals, and academic award ceremonies.
          </p>

          <div className="photo-grid">
            <div className="photo-card" onClick={() => setSelectedImage({ title: 'Annual Function Certificate & Prize Distribution', src: '/descriptive-image/students-holding-certificates-drawings-school.jpg', desc: 'Proud prize winners receiving academic achievement trophies.' })}>
              <div className="img-wrapper">
                <img src="/descriptive-image/students-holding-certificates-drawings-school.jpg" alt="Annual Prize Distribution" />
                <span className="category-badge">Annual Function</span>
              </div>
              <div className="caption-box">
                <h4>Prize Distribution & Student Felicitations</h4>
                <p>Recognizing outstanding scholastic and co-curricular achievements with shields and certificates.</p>
              </div>
            </div>

            <div className="photo-card" onClick={() => setSelectedImage({ title: '30-Year Celebration Milestone', src: '/descriptive-image/thirty-years-service-award.png', desc: 'Faculty, management, and distinguished guests celebrating 30 years of excellence.' })}>
              <div className="img-wrapper">
                <img src="/descriptive-image/thirty-years-service-award.png" alt="30 Years Celebration" />
                <span className="category-badge">Milestone</span>
              </div>
              <div className="caption-box">
                <h4>30-Year Jubilee Anniversary Gala</h4>
                <p>Tributes, alumni meet, and keynote addresses celebrating 3 decades of institutional service.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. SPORTS & CULTURAL ACTIVITIES */}
        <section id="sports-cultural" className="gallery-section-card">
          <span className="card-tag">4. Fitness & Traditions</span>
          <h2>Sports & Cultural Activities</h2>
          <p className="card-desc">
            Instilling team spirit, resilience, and cultural pride through annual athletics meets, cricket championships, yoga day, and national festivals.
          </p>

          <div className="photo-grid">
            <div className="photo-card" onClick={() => setSelectedImage({ title: 'Class X Board Toppers Honor Roll', src: '/descriptive-image/school-toppers-class-x-congratulations-2.png', desc: 'Merit felicitations for state and district rank holders.' })}>
              <div className="img-wrapper">
                <img src="/descriptive-image/school-toppers-class-x-congratulations-2.png" alt="Toppers Recognition" />
                <span className="category-badge">Academic Sports</span>
              </div>
              <div className="caption-box">
                <h4>Merit Felicitation & Academic Sports</h4>
                <p>Celebrating the discipline and rigorous dedication of our highest-ranking board scholars.</p>
              </div>
            </div>

            <div className="photo-card" onClick={() => setSelectedImage({ title: 'Sports Grounds & Drill Area', src: '/images/building.jpg', desc: 'Outdoor games arena for cricket, football, and athletics.' })}>
              <div className="img-wrapper">
                <img src="/images/building.jpg" alt="Sports Grounds" />
                <span className="category-badge">Athletics Ground</span>
              </div>
              <div className="caption-box">
                <h4>Athletic Drills & House Competitions</h4>
                <p>Inter-house tournaments in cricket, badminton, and sprint relays fostering healthy competition.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. EDUCATIONAL TOURS & EVENTS */}
        <section id="tours-events" className="gallery-section-card">
          <span className="card-tag">5. Experiential Learning</span>
          <h2>Educational Tours & Exhibitions</h2>
          <p className="card-desc">
            Hands-on learning through science model fairs, environmental drives, civic mock parliaments, and community health initiatives.
          </p>

          <div className="photo-grid">
            <div className="photo-card" onClick={() => setSelectedImage({ title: 'Parliament Model Exhibition', src: '/descriptive-image/students-with-parliament-models1.png', desc: 'Civic education exhibition with working models of democratic governance.' })}>
              <div className="img-wrapper">
                <img src="/descriptive-image/students-with-parliament-models1.png" alt="Parliament Exhibition" />
                <span className="category-badge">Exhibition</span>
              </div>
              <div className="caption-box">
                <h4>Social Science & Parliament Models</h4>
                <p>Students demonstrating intricate architectural models and parliamentary proceedings.</p>
              </div>
            </div>

            <div className="photo-card" onClick={() => setSelectedImage({ title: 'National Deworming & Health Day', src: '/descriptive-image/nation-deworming-day-celebration.png', desc: 'Community health camp and hygienic awareness sessions.' })}>
              <div className="img-wrapper">
                <img src="/descriptive-image/nation-deworming-day-celebration.png" alt="Health Day" />
                <span className="category-badge">Health Camp</span>
              </div>
              <div className="caption-box">
                <h4>Health & Environmental Awareness</h4>
                <p>Educating children on preventive healthcare, sanitation, nutrition, and environmental stewardship.</p>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-modal" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedImage(null)}>
              <X size={20} />
            </button>
            <img src={selectedImage.src} alt={selectedImage.title} />
            <div className="lightbox-details">
              <h3>{selectedImage.title}</h3>
              <p>{selectedImage.desc}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

